import { lerpColor } from '../../color';
import type { Rng } from '../../../core/rng';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, vignette, type Painter } from '../../art/paint';

const TAU = Math.PI * 2;
const FAR = hsl(215, 20, 16);
const CEIL = hsl(215, 16, 28);
const WIND = -0.2;

interface Layout {
  readonly portrait: boolean;
  readonly topMin: number;
  readonly topMax: number;
  readonly street: number;
  readonly lampX: number;
  readonly lampY: number;
  readonly lampS: number;
  readonly shopX0: number;
  readonly shopX1: number;
  readonly shopY: number;
  readonly shopBot: number;
  readonly rain: number;
}

function layoutOf(p: Painter): Layout {
  const portrait = p.w / p.h < 0.8;
  if (portrait) {
    return {
      portrait,
      topMin: p.h * 0.13,
      topMax: p.h * 0.27,
      street: p.h * 0.85,
      lampX: p.w * 0.84,
      lampY: p.h * 0.735,
      lampS: Math.max(12, p.unit * 0.068),
      shopX0: p.w * 0.04,
      shopX1: p.w * 0.36,
      shopY: p.h * 0.73,
      shopBot: p.h * 0.85,
      rain: 0.6,
    };
  }
  const shopW = Math.min(p.w * 0.17, p.unit * 0.56);
  return {
    portrait,
    topMin: p.h * 0.36,
    topMax: p.h * 0.6,
    street: p.h * 0.86,
    lampX: p.w * 0.875,
    lampY: p.h * 0.3,
    lampS: p.unit * 0.06,
    shopX0: p.w * 0.025,
    shopX1: p.w * 0.025 + shopW,
    shopY: p.h * 0.55,
    shopBot: p.h * 0.86,
    rain: 1,
  };
}

function sky(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const bg = look.palette.bg;
  const glow = lerpColor(lerpColor(FAR, CEIL, 0.55), look.palette.held, 0.05);
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(bg, look.palette.ink, 0.45)));
  g.addColorStop(Math.max(0.05, (lay.topMin / p.h) * 0.6), rgba(bg));
  g.addColorStop(Math.min(0.95, lay.topMax / p.h), rgba(glow));
  g.addColorStop(1, rgba(lerpColor(FAR, CEIL, 0.3)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
}

function lightning(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const rng = p.rng.fork('bolt');
  const x0 = p.w * (lay.portrait ? 0.3 : 0.13);
  const y0 = lay.portrait ? p.h * 0.1 : p.h * 0.22;
  const y1 = lay.topMin + (lay.topMax - lay.topMin) * 0.4;
  glowDot(ctx, x0, (y0 + y1) / 2, (y1 - y0) * 0.9, look.palette.rim, 0.07);
  const pts: number[] = [x0, y0];
  const steps = 6;
  let x = x0;
  for (let i = 1; i <= steps; i++) {
    x += rng.range(-1, 1) * p.unit * 0.035 + p.unit * 0.004;
    pts.push(x, y0 + ((y1 - y0) * i) / steps);
  }
  ctx.strokeStyle = rgba(look.palette.rim, 0.3);
  ctx.lineWidth = Math.max(1.5, p.unit * 0.005);
  ctx.lineJoin = 'round';
  ctx.beginPath();
  for (let i = 0; i < pts.length; i += 2) {
    if (i === 0) ctx.moveTo(pts[i]!, pts[i + 1]!);
    else ctx.lineTo(pts[i]!, pts[i + 1]!);
  }
  const fx = pts[6]!;
  const fy = pts[7]!;
  ctx.moveTo(fx, fy);
  ctx.lineTo(fx + p.unit * 0.05, fy + (y1 - y0) * 0.22);
  ctx.stroke();
}

function roof(p: Painter, rng: Rng, x: number, top: number, bw: number, tone: number): void {
  const ctx = p.ctx;
  ctx.fillStyle = rgba(tone);
  ctx.strokeStyle = rgba(tone);
  const roll = rng.float();
  if (roll < 0.3) {
    const ax = x + bw * rng.range(0.25, 0.75);
    ctx.lineWidth = Math.max(1, p.unit * 0.004);
    ctx.beginPath();
    ctx.moveTo(ax, top);
    ctx.lineTo(ax, top - p.unit * rng.range(0.04, 0.08));
    ctx.moveTo(ax - p.unit * 0.012, top - p.unit * 0.03);
    ctx.lineTo(ax + p.unit * 0.012, top - p.unit * 0.03);
    ctx.stroke();
  } else if (roll < 0.55) {
    const tw = Math.min(bw * 0.35, p.unit * 0.05);
    const tx = x + bw * rng.range(0.15, 0.6);
    const legs = p.unit * 0.015;
    ctx.fillRect(tx, top - legs - tw * 0.9, tw, tw * 0.9);
    ctx.beginPath();
    ctx.moveTo(tx - tw * 0.08, top - legs - tw * 0.9);
    ctx.lineTo(tx + tw / 2, top - legs - tw * 1.35);
    ctx.lineTo(tx + tw * 1.08, top - legs - tw * 0.9);
    ctx.fill();
    ctx.lineWidth = Math.max(1, p.unit * 0.003);
    ctx.beginPath();
    ctx.moveTo(tx + tw * 0.15, top);
    ctx.lineTo(tx + tw * 0.15, top - legs);
    ctx.moveTo(tx + tw * 0.85, top);
    ctx.lineTo(tx + tw * 0.85, top - legs);
    ctx.stroke();
  } else if (roll < 0.75) {
    const step = bw * rng.range(0.4, 0.7);
    ctx.fillRect(x + (bw - step) / 2, top - p.unit * 0.03, step, p.unit * 0.03);
  }
}

function windows(p: Painter, look: Look, rng: Rng, x: number, top: number, bw: number, bottom: number, odds: number): void {
  const ctx = p.ctx;
  const cw = p.unit * 0.02;
  const ch = p.unit * 0.028;
  const gx = p.unit * 0.018;
  const gy = p.unit * 0.022;
  const cols = Math.max(1, Math.floor((bw - gx) / (cw + gx)));
  const pad = (bw - cols * (cw + gx) + gx) / 2;
  for (let y = top + gy * 1.5; y + ch < bottom; y += ch + gy) {
    for (let c = 0; c < cols; c++) {
      if (!rng.chance(odds)) continue;
      const wx = x + pad + c * (cw + gx);
      if (calmAt(p, wx + cw / 2, y + ch / 2, 1.0)) continue;
      const warm = rng.chance(0.85);
      ctx.fillStyle = rgba(warm ? look.palette.held : look.palette.incoming.circle, rng.range(0.22, 0.5));
      ctx.fillRect(wx, y, cw, ch);
      if (warm && rng.chance(0.3)) {
        ctx.fillStyle = rgba(FAR, 0.6);
        ctx.fillRect(wx, y, cw, ch * rng.range(0.3, 0.6));
      }
    }
  }
}

function skyline(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const rng = p.rng.fork('skyline');
  let x = -rng.range(0, p.unit * 0.1);
  while (x < p.w) {
    const bw = p.unit * rng.range(0.1, 0.22);
    const top = rng.range(lay.topMin, lay.topMax);
    roof(p, rng, x, top, bw, FAR);
    ctx.fillStyle = rgba(FAR);
    ctx.fillRect(x, top, bw, p.h - top);
    ctx.strokeStyle = rgba(CEIL, 0.5);
    ctx.lineWidth = Math.max(1, p.unit * 0.003);
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x + bw, top);
    ctx.stroke();
    windows(p, look, rng, x, top, bw, lay.street, 0.13);
    x += bw + p.unit * rng.range(-0.01, 0.012);
  }
}

function fog(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const rng = p.rng.fork('fog');
  const tone = lerpColor(CEIL, look.palette.rim, 0.12);
  const bands = [lay.topMax, (lay.topMax + lay.street) / 2, lay.street - p.unit * 0.04];
  for (const y of bands) {
    for (let i = 0; i < 3; i++) {
      const x = p.w * (i / 2) + rng.range(-0.1, 0.1) * p.w;
      const r = p.unit * rng.range(0.16, 0.24);
      ctx.save();
      ctx.translate(x, y + rng.range(-0.3, 0.3) * r);
      ctx.scale(p.w / (r * 2.2), 1);
      glowDot(ctx, 0, 0, r, tone, 0.2);
      ctx.restore();
    }
  }
}

function street(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const rng = p.rng.fork('street');
  const curb = lay.street;
  const walk = p.unit * 0.035;
  ctx.fillStyle = rgba(lerpColor(FAR, look.palette.bg, 0.35));
  ctx.fillRect(0, curb, p.w, walk);
  const road = ctx.createLinearGradient(0, curb + walk, 0, p.h);
  road.addColorStop(0, rgba(lerpColor(look.palette.bg, CEIL, 0.18)));
  road.addColorStop(1, rgba(lerpColor(look.palette.bg, look.palette.ink, 0.35)));
  ctx.fillStyle = road;
  ctx.fillRect(0, curb + walk, p.w, p.h - curb - walk);
  ctx.strokeStyle = rgba(lerpColor(CEIL, look.palette.rim, 0.2), 0.45);
  ctx.lineWidth = Math.max(1, p.unit * 0.004);
  ctx.beginPath();
  ctx.moveTo(0, curb);
  ctx.lineTo(p.w, curb);
  ctx.moveTo(0, curb + walk);
  ctx.lineTo(p.w, curb + walk);
  ctx.stroke();
  const n = Math.max(3, Math.round(p.w / (p.unit * 0.42)));
  for (let i = 0; i < n; i++) {
    const x = ((i + rng.range(0.15, 0.85)) / n) * p.w;
    const y = rng.range(curb + walk + p.unit * 0.03, Math.max(curb + walk + p.unit * 0.04, p.h - p.unit * 0.03));
    const rx = p.unit * rng.range(0.07, 0.15) * (lay.portrait ? 0.7 : 1);
    const ry = rx * rng.range(0.14, 0.2);
    puddle(p, look, rng, x, y, rx, ry);
  }
}

function puddle(p: Painter, look: Look, rng: Rng, x: number, y: number, rx: number, ry: number): void {
  const ctx = p.ctx;
  ctx.fillStyle = rgba(lerpColor(FAR, look.palette.incoming.circle, 0.1), 0.6);
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(look.palette.rim, 0.14);
  ctx.lineWidth = 1;
  ctx.stroke();
  const rings = 1 + Math.floor(rng.float() * 2);
  for (let i = 0; i < rings; i++) {
    const px = x + rng.range(-0.5, 0.5) * rx;
    const pr = rx * rng.range(0.12, 0.22);
    ctx.strokeStyle = rgba(look.palette.rim, 0.16);
    ctx.beginPath();
    ctx.ellipse(px, y, pr, pr * 0.3, 0, 0, TAU);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(px, y, pr * 1.8, pr * 0.55, 0, 0, TAU);
    ctx.strokeStyle = rgba(look.palette.rim, 0.08);
    ctx.stroke();
  }
}

function reflection(p: Painter, x: number, top: number, width: number, color: number, alpha: number): void {
  const ctx = p.ctx;
  const bottom = p.h;
  const g = ctx.createLinearGradient(0, top, 0, bottom);
  g.addColorStop(0, rgba(color, alpha));
  g.addColorStop(1, rgba(color, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  const steps = 10;
  for (let i = 0; i <= steps; i++) {
    const y = top + ((bottom - top) * i) / steps;
    const k = width * (0.5 + 0.5 * (i / steps)) * (1 + 0.35 * Math.sin(i * 1.9));
    if (i === 0) ctx.moveTo(x - k, y);
    else ctx.lineTo(x - k, y);
  }
  for (let i = steps; i >= 0; i--) {
    const y = top + ((bottom - top) * i) / steps;
    const k = width * (0.5 + 0.5 * (i / steps)) * (1 + 0.35 * Math.cos(i * 2.3));
    ctx.lineTo(x + k, y);
  }
  ctx.closePath();
  ctx.fill();
}

function shop(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const pal = look.palette;
  const x0 = lay.shopX0;
  const x1 = lay.shopX1;
  const sw = x1 - x0;
  const wallTop = lay.portrait ? lay.shopY - p.h * 0.02 : lay.shopY - p.h * 0.14;
  const wall = lerpColor(pal.bg, pal.ink, 0.4);
  ctx.fillStyle = rgba(wall);
  ctx.fillRect(x0 - sw * 0.08, wallTop, sw * 1.16, lay.street - wallTop);
  ctx.fillStyle = rgba(lerpColor(wall, CEIL, 0.35));
  ctx.fillRect(x0 - sw * 0.12, wallTop - p.unit * 0.012, sw * 1.24, p.unit * 0.014);
  if (!lay.portrait) {
    const uw = sw * 0.22;
    const uh = (lay.shopY - wallTop) * 0.5;
    for (const ux of [x0 + sw * 0.18, x0 + sw * 0.6]) {
      ctx.fillStyle = rgba(pal.held, ux < x0 + sw * 0.4 ? 0.34 : 0.12);
      ctx.fillRect(ux, wallTop + uh * 0.45, uw, uh);
      ctx.strokeStyle = rgba(pal.ink, 0.8);
      ctx.lineWidth = Math.max(1, p.unit * 0.004);
      ctx.strokeRect(ux, wallTop + uh * 0.45, uw, uh);
    }
  }
  const valance = Math.max(6, (lay.shopBot - lay.shopY) * 0.12);
  const drop = Math.max(8, (lay.shopBot - lay.shopY) * 0.14);
  const glassTop = lay.shopY + drop + valance + p.unit * 0.012;
  const glassBot = lay.shopBot - p.unit * 0.02;
  const pane = ctx.createLinearGradient(0, glassTop, 0, glassBot);
  pane.addColorStop(0, rgba(pal.held, 0.4));
  pane.addColorStop(1, rgba(pal.heldShade, 0.22));
  ctx.fillStyle = pane;
  ctx.fillRect(x0 + sw * 0.06, glassTop, sw * 0.88, glassBot - glassTop);
  ctx.strokeStyle = rgba(pal.ink, 0.85);
  ctx.lineWidth = Math.max(1.5, p.unit * 0.006);
  ctx.strokeRect(x0 + sw * 0.06, glassTop, sw * 0.88, glassBot - glassTop);
  ctx.beginPath();
  ctx.moveTo(x0 + sw * 0.5, glassTop);
  ctx.lineTo(x0 + sw * 0.5, glassBot);
  ctx.stroke();
  const stripes = 4;
  const slant = sw * 0.06;
  for (let i = 0; i < stripes; i++) {
    const a = i / stripes;
    const b = (i + 1) / stripes;
    const topA = x0 + slant + (sw - 2 * slant) * a;
    const topB = x0 + slant + (sw - 2 * slant) * b;
    ctx.fillStyle = rgba(lerpColor(pal.bg, pal.spot, i % 2 === 0 ? 0.3 : 0.22));
    ctx.beginPath();
    ctx.moveTo(topA, lay.shopY);
    ctx.lineTo(topB, lay.shopY);
    ctx.lineTo(x0 + sw * b, lay.shopY + drop);
    ctx.lineTo(x0 + sw * a, lay.shopY + drop);
    ctx.closePath();
    ctx.fill();
  }
  const scallops = 6;
  const sc = sw / scallops;
  ctx.fillStyle = rgba(lerpColor(pal.bg, pal.spot, 0.27));
  ctx.beginPath();
  ctx.moveTo(x0, lay.shopY + drop);
  ctx.lineTo(x1, lay.shopY + drop);
  ctx.lineTo(x1, lay.shopY + drop + valance * 0.5);
  for (let i = scallops - 1; i >= 0; i--) {
    ctx.arc(x0 + sc * (i + 0.5), lay.shopY + drop + valance * 0.5, sc / 2, 0, Math.PI);
  }
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(pal.ink, 0.8);
  ctx.lineWidth = Math.max(1.2, p.unit * 0.005);
  ctx.stroke();
  ctx.strokeStyle = rgba(pal.spot, 0.2);
  ctx.lineWidth = Math.max(1, p.unit * 0.003);
  ctx.beginPath();
  ctx.moveTo(x0 + slant, lay.shopY);
  ctx.lineTo(x1 - slant, lay.shopY);
  ctx.stroke();
}

function drips(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const rng = p.rng.fork('drips');
  const sw = lay.shopX1 - lay.shopX0;
  const edge = lay.shopY + Math.max(8, (lay.shopBot - lay.shopY) * 0.14) + Math.max(6, (lay.shopBot - lay.shopY) * 0.12);
  ctx.strokeStyle = rgba(lerpColor(look.palette.rim, look.palette.incoming.circle, 0.5), 0.22);
  ctx.lineWidth = Math.max(1, p.unit * 0.003);
  ctx.lineCap = 'round';
  let x = lay.shopX0 + rng.range(0.02, 0.08) * sw;
  while (x < lay.shopX1) {
    const len = (lay.street - edge) * rng.range(0.15, 0.55);
    const y0 = edge + rng.range(0, 0.1) * (lay.street - edge);
    ctx.beginPath();
    ctx.moveTo(x, y0);
    ctx.lineTo(x, y0 + len);
    ctx.stroke();
    ctx.fillStyle = rgba(look.palette.incoming.circle, 0.3);
    ctx.beginPath();
    ctx.arc(x, y0 + len + 2, Math.max(1.2, p.unit * 0.004), 0, TAU);
    ctx.fill();
    x += sw * rng.range(0.07, 0.16);
  }
}

function lamp(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const pal = look.palette;
  const x = lay.lampX;
  const y = lay.lampY;
  const s = lay.lampS;
  const base = lay.street + p.unit * 0.02;
  const post = lerpColor(pal.ink, FAR, 0.25);
  const coneW = lay.portrait ? p.w * 0.14 : p.unit * 0.34;
  const cone = ctx.createLinearGradient(0, y + s, 0, base);
  cone.addColorStop(0, rgba(pal.held, 0.12));
  cone.addColorStop(1, rgba(pal.held, 0.02));
  ctx.fillStyle = cone;
  ctx.beginPath();
  ctx.moveTo(x - s * 0.7, y + s);
  ctx.lineTo(x + s * 0.7, y + s);
  ctx.lineTo(x + coneW, base);
  ctx.lineTo(x - coneW, base);
  ctx.closePath();
  ctx.fill();
  ctx.save();
  ctx.translate(x, base + p.unit * 0.01);
  ctx.scale(1, 0.16);
  glowDot(ctx, 0, 0, coneW * 1.3, pal.held, 0.3);
  ctx.restore();
  reflection(p, x, base + p.unit * 0.02, s * 0.7, pal.held, 0.3);
  glowDot(ctx, x, y, p.unit * (lay.portrait ? 0.3 : 0.42), pal.held, 0.3);
  ctx.fillStyle = rgba(post);
  const pw = Math.max(2.5, s * 0.32);
  ctx.beginPath();
  ctx.moveTo(x - pw * 0.4, y + s * 1.3);
  ctx.lineTo(x + pw * 0.4, y + s * 1.3);
  ctx.lineTo(x + pw * 0.55, base - s * 0.9);
  ctx.lineTo(x + pw * 1.3, base - s * 0.4);
  ctx.lineTo(x + pw * 1.5, base);
  ctx.lineTo(x - pw * 1.5, base);
  ctx.lineTo(x - pw * 1.3, base - s * 0.4);
  ctx.lineTo(x - pw * 0.55, base - s * 0.9);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(x - s * 1.1, y + s * 2.3, s * 2.2, Math.max(1.5, s * 0.1));
  ctx.fillRect(x - pw * 0.8, y + s * 1.25, pw * 1.6, s * 0.3);
  ctx.strokeStyle = rgba(pal.held, 0.22);
  ctx.lineWidth = Math.max(1, pw * 0.18);
  ctx.beginPath();
  ctx.moveTo(x - pw * 0.25, y + s * 1.6);
  ctx.lineTo(x - pw * 0.35, base - s * 1);
  ctx.stroke();
  glowDot(ctx, x, y, s * 2.4, pal.heldLight, 0.4);
  ctx.fillStyle = rgba(pal.heldLight, 0.58);
  ctx.beginPath();
  ctx.moveTo(x - s, y - s * 1.05);
  ctx.lineTo(x + s, y - s * 1.05);
  ctx.lineTo(x + s * 0.7, y + s);
  ctx.lineTo(x - s * 0.7, y + s);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(post);
  ctx.lineWidth = Math.max(1.2, s * 0.12);
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x, y - s * 1.05);
  ctx.lineTo(x, y + s);
  ctx.stroke();
  ctx.fillStyle = rgba(post);
  ctx.beginPath();
  ctx.moveTo(x - s * 1.3, y - s * 1.0);
  ctx.lineTo(x, y - s * 1.75);
  ctx.lineTo(x + s * 1.3, y - s * 1.0);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y - s * 1.85, s * 0.16, 0, TAU);
  ctx.fill();
  ctx.fillRect(x - s * 0.8, y + s, s * 1.6, s * 0.28);
}

function rain(p: Painter, look: Look, lay: Layout): void {
  const ctx = p.ctx;
  const rng = p.rng.fork('rain');
  const n = Math.round(((p.w * p.h) / 2600) * lay.rain);
  const cool = lerpColor(look.palette.rim, look.palette.incoming.circle, 0.5);
  const warm = lerpColor(look.palette.heldLight, look.palette.rim, 0.3);
  const coneW = lay.portrait ? p.w * 0.14 : p.unit * 0.34;
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(1.1, p.unit * 0.004);
  for (let i = 0; i < n; i++) {
    const x = rng.float() * p.w;
    const y = rng.float() * p.h;
    const len = p.unit * rng.range(0.022, 0.06);
    const tilt = WIND + rng.range(-0.06, 0.06);
    const alpha = rng.range(0.1, 0.24);
    if (calmAt(p, x, y, 0.95) && rng.float() > 0.12) continue;
    const t = (y - lay.lampY) / Math.max(1, lay.street - lay.lampY);
    const lit = t > 0 && t < 1 && Math.abs(x - lay.lampX) < lay.lampS + coneW * t;
    ctx.strokeStyle = rgba(lit ? warm : cool, lit ? Math.min(0.25, alpha + 0.06) : alpha);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.sin(tilt) * len, y + Math.cos(tilt) * len);
    ctx.stroke();
  }
  const splashes = Math.round((p.w / (p.unit * 0.12)) * lay.rain);
  ctx.strokeStyle = rgba(look.palette.rim, 0.18);
  for (let i = 0; i < splashes; i++) {
    const x = rng.float() * p.w;
    const y = rng.range(lay.street + p.unit * 0.04, p.h - p.unit * 0.01);
    const r = p.unit * rng.range(0.008, 0.014);
    ctx.beginPath();
    ctx.moveTo(x - r, y);
    ctx.lineTo(x - r * 1.4, y - r * 1.1);
    ctx.moveTo(x, y - r * 0.2);
    ctx.lineTo(x, y - r * 1.5);
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + r * 1.4, y - r * 1.1);
    ctx.stroke();
  }
}

export function paint(p: Painter, look: Look): void {
  const lay = layoutOf(p);
  sky(p, look, lay);
  if (p.level >= 4) lightning(p, look, lay);
  skyline(p, look, lay);
  if (p.level >= 2) fog(p, look, lay);
  street(p, look, lay);
  shop(p, look, lay);
  reflection(p, (lay.shopX0 + lay.shopX1) / 2, lay.street + p.unit * 0.04, (lay.shopX1 - lay.shopX0) * 0.25, look.palette.held, 0.16);
  if (p.level >= 3) drips(p, look, lay);
  lamp(p, look, lay);
  rain(p, look, lay);
  calmCenter(p, look.palette.bg, 0.6, 1.15);
  grain(p, 0.05, true);
  vignette(p, look.palette.ink, 0.5);
}
